import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, Grid } from '@mui/joy';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { supabase } from '../supabaseClient';
import MetricCard from './MetricCard';
import { CardSkeleton } from './LoadingSkeleton';
import { getChartOptions, getChartColors } from '../utils/chartConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

const PermitAnalytics = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('project_records')
        .select('*')
        .eq('archived', false)
        .in('record_type', ['Permit', 'Plans']);
      if (error) console.error('Error fetching records:', error);
      else setRecords(data);
      setLoading(false);
    };

    fetchRecords();
  }, []);

  const statusCounts = { 'In Progress': 0, 'In Review': 0, 'Active': 0, 'Certified': 0 };
  const agencyCounts = {};
  let expiringCount = 0;

  records.forEach(record => {
    const { start_date, application_date, issue_date, expiration_date, agency } = record;

    if (start_date && application_date && !issue_date && !expiration_date) {
      statusCounts['In Review']++;
    } else if (start_date && !application_date && !issue_date && !expiration_date) {
      statusCounts['In Progress']++;
    } else if (issue_date && expiration_date) {
      statusCounts['Active']++;
    } else if (expiration_date && !issue_date) {
      statusCounts['Active']++;
    }

    if (agency) {
      agencyCounts[agency] = (agencyCounts[agency] || 0) + 1;
    }

    if (record.certified) {
      statusCounts['Certified']++;
    }

    // Check for expiring permits
    if (expiration_date) {
      const exp = new Date(expiration_date);
      const now = new Date();
      const in30Days = new Date(now);
      in30Days.setDate(now.getDate() + 30);
      if (exp >= now && exp <= in30Days) {
        expiringCount++;
      }
    }
  });

  const colors = getChartColors();
  
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Record Count',
        data: Object.values(statusCounts),
        backgroundColor: [
          colors.primary + '90',
          colors.secondary + '90', 
          colors.info + '90',
          colors.warning + '90'
        ],
        borderColor: [
          colors.primary,
          colors.secondary,
          colors.info,
          colors.warning
        ],
        borderWidth: 2,
        hoverBackgroundColor: [
          colors.primary + 'CC',
          colors.secondary + 'CC',
          colors.info + 'CC', 
          colors.warning + 'CC'
        ],
      },
    ],
  };

  const agencyChartData = {
    labels: Object.keys(agencyCounts),
    datasets: [
      {
        label: 'Records by Agency',
        data: Object.values(agencyCounts),
        backgroundColor: Object.keys(agencyCounts).map((_, i) => {
          const colorKeys = ['primary', 'secondary', 'success', 'info', 'warning'];
          return colors[colorKeys[i % colorKeys.length]] + '80';
        }),
        borderColor: Object.keys(agencyCounts).map((_, i) => {
          const colorKeys = ['primary', 'secondary', 'success', 'info', 'warning'];
          return colors[colorKeys[i % colorKeys.length]];
        }),
        borderWidth: 2,
        borderRadius: 4,
      },
    ],
  };

  if (loading) {
    return (
      <Card sx={{ p: 3, my: 2, overflow: 'visible' }} className="fade-in">
        <Typography level="h3" sx={{ mb: 3, color: 'success.700' }}>
          📊 Permit Analytics Overview
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid xs={12} sm={6} md={3}>
            <CardSkeleton />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <CardSkeleton />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <CardSkeleton />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <CardSkeleton />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <CardSkeleton />
          <CardSkeleton />
        </Box>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        p: 3, 
        my: 2, 
        overflow: 'visible',
        background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
        border: '1px solid',
        borderColor: 'success.200',
      }} 
      className="fade-in-up"
    >
      <Typography 
        level="h3" 
        sx={{ 
          mb: 3, 
          color: 'success.700',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
          analytics
        </span>
        Permit Analytics Overview
      </Typography>

      {/* Key Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Records"
            value={records.length}
            icon="description"
            gradientColors={[colors.primary, colors.secondary]}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Permits"
            value={statusCounts['Active']}
            icon="verified"
            gradientColors={[colors.success, colors.primary]}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="In Review"
            value={statusCounts['In Review']}
            icon="schedule"
            gradientColors={[colors.info, '#138496']}
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Expiring Soon"
            value={expiringCount}
            icon="warning"
            gradientColors={[colors.warning, '#f7931e']}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={4}>
        <Grid xs={12} lg={6}>
          <Card 
            sx={{ 
              p: 3, 
              height: '400px',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: 'success.100',
            }}
            className="hover-lift"
          >
            <Typography level="h4" sx={{ mb: 2, color: 'success.700' }}>
              📈 Records by Status
            </Typography>
            <Box sx={{ height: '250px' }}>
              <Pie
                data={statusChartData}
                options={{
                  ...getChartOptions(),
                  maintainAspectRatio: false,
                  plugins: {
                    ...getChartOptions().plugins,
                    datalabels: {
                      display: false,
                    },
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 15,
                        usePointStyle: true,
                        font: {
                          family: 'Inter, system-ui, sans-serif',
                          size: 12,
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.25', borderRadius: 'sm' }}>
              <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
                <strong>In Progress:</strong> Has a start date only. &nbsp;
                <strong>In Review:</strong> Application submitted, awaiting approval. &nbsp;
                <strong>Active:</strong> Issued and valid. &nbsp;
                <strong>Certified:</strong> Engineer certified.
              </Typography>
            </Box>
          </Card>
        </Grid>
        
        <Grid xs={12} lg={6}>
          <Card 
            sx={{ 
              p: 3, 
              height: '400px',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: 'success.100',
            }}
            className="hover-lift"
          >
            <Typography level="h4" sx={{ mb: 2, color: 'success.700' }}>
              🏛️ Records by Agency
            </Typography>
            <Box sx={{ height: '320px' }}>
              <Bar 
                data={agencyChartData} 
                options={{
                  ...getChartOptions(),
                  maintainAspectRatio: false,
                  plugins: {
                    ...getChartOptions().plugins,
                    datalabels: {
                      display: false,
                    },
                  },
                  scales: {
                    ...getChartOptions().scales,
                    x: {
                      ...getChartOptions().scales.x,
                      ticks: {
                        ...getChartOptions().scales.x.ticks,
                        maxRotation: 45,
                      },
                    },
                  },
                }}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Card>
  );
};

export default PermitAnalytics;